import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import { mdiPlus } from '@mdi/js';
import { useApolloClient } from '@apollo/client/react';
import { useNavigate } from 'react-router';

import { ActionButtonDropdown } from 'common/components/ActionButtonDropdown';
import { FilePicker } from 'common/components/FilePicker';
import { Icon } from 'common/components/Icon';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { IGlobalDrawerType } from 'common/types';
import { UserImportBlockedDialog } from 'user/components/UserImportBlockedDialog';
import { UserImportModal } from 'user/components/UserImportModal';
import { useUserImportGuard } from 'user/hooks/useUserImportGuard';
import { useUserImport } from 'user/hooks/useUserImport';
import { EUsersImportType } from 'user/types';
import { getUrl } from 'route/utils/getUrl';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { useAppPermissions } from 'user/hooks/usePermissions';

export const AddUsersButton = () => {
    const [translate] = useTranslation();
    const navigate = useNavigate();
    const client = useApolloClient();
    const [displaySnackbar] = useSnackbar();
    const { openGlobalDrawer } = useGlobalDrawer();
    const isUserActivePage = !!useRouteMatch('USER_MANAGEMENT_LIST');
    const { canImport = false } = useAppPermissions('user', 'management');

    const {
        completeImportData,
        importModalOpen,
        handleFileUpload,
        handleImportModalClose,
    } = useUserImport({
        onUploadError: (_fileName, message) => {
            displaySnackbar(message, { variant: 'error' });
        },
    });

    const {
        guardedImportClick,
        checkingImports,
        importBlockedDialogOpen,
        handleImportBlockedClose,
    } = useUserImportGuard({ canImport });

    const handleCreateUser = (): void => {
        if (!isUserActivePage) navigate(getUrl('USER_MANAGEMENT_LIST'));

        client.cache.evict({ fieldName: 'paginatedUsers' });
        client.cache.gc();
    };

    const handleOpenCreateDrawer = () => {
        openGlobalDrawer({
            type: IGlobalDrawerType.UserCreate,
            props: {
                onCreate: handleCreateUser,
            },
        });
    };

    const handleImportClick = () => guardedImportClick('users-import-csv');

    const menuItems = [
        <MenuItem key="new-user" onClick={handleOpenCreateDrawer}>
            {translate('usersManagementPage.newUser')}
        </MenuItem>,

        canImport ? (
            <MenuItem
                key="user-import-csv-menu-item"
                onClick={handleImportClick}
            >
                {translate('usersManagementPage.importFile')}
            </MenuItem>
        ) : null,
    ].filter(Boolean);

    return (
        <>
            <ActionButtonDropdown
                disabled={checkingImports}
                menuItems={menuItems.length > 1 ? menuItems : undefined}
                onClick={handleOpenCreateDrawer}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {checkingImports ? (
                        <CircularProgress color="inherit" size={20} />
                    ) : (
                        <Icon path={mdiPlus} />
                    )}
                </Box>

                {translate('usersManagementPage.newUser')}
            </ActionButtonDropdown>

            {canImport && (
                <FilePicker
                    id="users-import-csv"
                    inputProps={{ accept: '.csv' }}
                    sx={{
                        position: 'absolute',
                        overflow: 'hidden',
                        width: 0,
                        height: 0,
                    }}
                    onUpload={handleFileUpload}
                />
            )}

            <UserImportModal
                importData={completeImportData}
                importType={EUsersImportType.Organisation}
                open={importModalOpen}
                onClose={handleImportModalClose}
            />

            <UserImportBlockedDialog
                open={importBlockedDialogOpen}
                onClose={handleImportBlockedClose}
            />
        </>
    );
};
